import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { performanceService } from "./services/performanceService";
import { supabase } from "@/integrations/supabase/client";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

interface AddGoalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGoalAdded: () => void;
  selectedTeamMemberId?: string;
}

type GoalCategory = 'financial' | 'project_delivery' | 'customer_impact' | 'innovation' | 'leadership_behaviors';

export const AddGoalDialog = ({ open, onOpenChange, onGoalAdded, selectedTeamMemberId }: AddGoalDialogProps) => {
  const { activeYear } = usePerformanceYear();
  const [formData, setFormData] = useState({
    goal_title: "",
    goal_description: "",
    goal_category: "" as GoalCategory | "",
    goal_type: "",
    target_value: "",
    status: "",
    priority: "",
    start_date: "",
    target_date: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState("");

  const validateForm = () => {
    // Clear previous error
    setValidationError("");

    // Check required fields
    if (!formData.goal_title.trim()) {
      setValidationError("Goal Title is required");
      return false;
    }

    if (!formData.goal_category) {
      setValidationError("Goal Category is required");
      return false;
    }

    if (!formData.goal_type) {
      setValidationError("Goal Type is required");
      return false;
    }

    if (!formData.status) {
      setValidationError("Progress status is required");
      return false;
    }

    if (!formData.priority) {
      setValidationError("Priority is required");
      return false;
    }

    if (!formData.start_date) {
      setValidationError("Start Date is required");
      return false;
    }

    if (!formData.target_date) {
      setValidationError("Target Date is required");
      return false;
    }

    // Validate date order
    if (formData.start_date && formData.target_date) {
      const startDate = new Date(formData.start_date);
      const targetDate = new Date(formData.target_date);
      if (startDate >= targetDate) {
        setValidationError("Target Date must be after Start Date");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    // Validate form first
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // Get the current authenticated user (this will be the supervisor)
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        setValidationError("Authentication required");
        return;
      }

      let goalUserId: string;
      let supervisorId: string | null;

      // Determine the context and set user_id and supervisor_id accordingly
      if (selectedTeamMemberId) {
        // Manage Team Members context: Creating goal for selected team member
        goalUserId = selectedTeamMemberId;
        supervisorId = user.id; // Current user is the supervisor
      } else {
        // Personal Goals context: Creating goal for current user
        goalUserId = user.id;
        
        // Get the current user's supervisor from the users table
        const { data: userProfile, error: profileError } = await supabase
          .from('users')
          .select('supervisor_id')
          .eq('id', user.id)
          .maybeSingle();

        if (profileError) {
          console.error("Profile error:", profileError);
          setValidationError("Failed to fetch supervisor information");
          return;
        }

        supervisorId = userProfile?.supervisor_id ?? null;
      }

      const goalData = {
        user_id: goalUserId,
        goal_title: formData.goal_title.trim(),
        goal_description: formData.goal_description.trim() || undefined,
        goal_category: formData.goal_category as GoalCategory,
        goal_type: formData.goal_type as 'performance' | 'behavioral',
        target_value: formData.target_value.trim() || undefined,
        current_progress: 0,
        status: formData.status as 'active' | 'paused' | 'completed' | 'cancelled',
        priority: formData.priority as 'high' | 'medium' | 'low',
        start_date: formData.start_date,
        target_date: formData.target_date,
        supervisor_id: supervisorId,
        year: activeYear,
      };

      console.log("Submitting goal data:", goalData);

      await performanceService.createPerformanceGoal(goalData);
      
      toast.success("Goal created successfully!");
      onGoalAdded();
      onOpenChange(false);
      
      // Reset form
      resetForm();
    } catch (error) {
      console.error("Error creating goal:", error);
      setValidationError("Failed to create goal. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      goal_title: "",
      goal_description: "",
      goal_category: "",
      goal_type: "",
      target_value: "",
      status: "",
      priority: "",
      start_date: "",
      target_date: "",
    });
    setValidationError("");
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    // Clear validation error when user makes changes
    if (validationError) {
      setValidationError("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white border-gray-400">
        <DialogHeader>
          <DialogTitle className="text-black">Add New Goal</DialogTitle>
          {validationError && (
            <div className="text-red-600 text-sm mt-2 font-medium">
              {validationError}
            </div>
          )}
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-center gap-3">
              <Label htmlFor="goal_title" className="text-black whitespace-nowrap min-w-[90px]">Goal Title *</Label>
              <Input
                id="goal_title"
                value={formData.goal_title}
                onChange={(e) => handleInputChange("goal_title", e.target.value)}
                className="bg-white border-gray-400 text-black flex-1"
              />
            </div>

            <div className="flex items-center gap-3">
              <Label htmlFor="goal_type" className="text-black whitespace-nowrap min-w-[90px]">Goal Type *</Label>
              <Select value={formData.goal_type} onValueChange={(value) => handleInputChange("goal_type", value)}>
                <SelectTrigger className="bg-white border-gray-400 text-black flex-1">
                  <SelectValue placeholder="Select goal type" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-400 text-black">
                  <SelectItem value="performance">Performance</SelectItem>
                  <SelectItem value="behavioral">Behavioral</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Label htmlFor="goal_description" className="text-black whitespace-nowrap min-w-[90px] mt-2">Description</Label>
            <Textarea
              id="goal_description"
              value={formData.goal_description}
              onChange={(e) => handleInputChange("goal_description", e.target.value)}
              className="bg-white border-gray-400 text-black flex-1"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-center gap-3">
              <Label htmlFor="goal_category" className="text-black whitespace-nowrap min-w-[90px]">Category *</Label>
              <Select value={formData.goal_category} onValueChange={(value) => handleInputChange("goal_category", value)}>
                <SelectTrigger className="bg-white border-gray-400 text-black flex-1">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-400 text-black">
                  <SelectItem value="financial">Financial</SelectItem>
                  <SelectItem value="project_delivery">Project Delivery</SelectItem>
                  <SelectItem value="customer_impact">Customer Impact</SelectItem>
                  <SelectItem value="innovation">Innovation</SelectItem>
                  <SelectItem value="leadership_behaviors">Leadership Behaviours</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <Label htmlFor="target_value" className="text-black whitespace-nowrap min-w-[90px]">Target</Label>
              <Input
                id="target_value"
                value={formData.target_value}
                onChange={(e) => handleInputChange("target_value", e.target.value)}
                className="bg-white border-gray-400 text-black flex-1"
                placeholder="Enter target value"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-center gap-3">
              <Label htmlFor="status" className="text-black whitespace-nowrap min-w-[90px]">Progress *</Label>
              <Select value={formData.status} onValueChange={(value) => handleInputChange("status", value)}>
                <SelectTrigger className="bg-white border-gray-400 text-black flex-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-400 text-black">
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3">
              <Label htmlFor="priority" className="text-black whitespace-nowrap min-w-[90px]">Priority *</Label>
              <Select value={formData.priority} onValueChange={(value) => handleInputChange("priority", value)}>
                <SelectTrigger className="bg-white border-gray-400 text-black flex-1">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-400 text-black">
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-center gap-3">
              <Label className="text-black whitespace-nowrap min-w-[90px]">Start Date *</Label>
              <div className="flex-1">
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleInputChange("start_date", e.target.value)}
                  className="bg-white border-gray-400 text-black"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Label className="text-black whitespace-nowrap min-w-[90px]">Target Date *</Label>
              <div className="flex-1">
                <Input
                  type="date"
                  value={formData.target_date}
                  onChange={(e) => handleInputChange("target_date", e.target.value)}
                  className="bg-white border-gray-400 text-black"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              resetForm();
            }}
            className="border-gray-400 text-black hover:bg-primary/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className="bg-primary hover:bg-primary/90 text-black"
          >
            {isLoading ? "Saving..." : "Save Goal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
